import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import Layout from "@/components/Layout";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { usePortal, PORTAL_RENTALS, PORTAL_TRUCKING, PORTAL_LABELS } from "@/context/PortalContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import {
  Table, TableHeader, TableRow, TableHead, TableBody, TableCell,
} from "@/components/ui/table";
import { Checkbox } from "@/components/ui/checkbox";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import {
  Droplets, Trash2, Wrench, Truck, Car, ClipboardList, Users, DollarSign,
  CheckCircle2, AlertCircle, AlertTriangle, Camera, FileText, Settings, Plus, Trash, Edit, Save, RefreshCw, KeyRound,
  Share2, Mail, Copy, MessageCircle,
} from "lucide-react";
import { STATUS_LABELS, STATUS_COLORS, ROLE_LABELS, formatXCD } from "@/lib/constants";
import { invalidateAll } from "@/lib/useLiveData";

const SERVICE_ICONS = {
  water: Droplets, bin: Trash2, diagnostics: Wrench, trucking: Truck, rental: Car,
};
const SERVICE_LABELS = {
  water: "Water Delivery", bin: "Bin Rental", diagnostics: "Diagnostics",
  trucking: "Trucking", rental: "Car Rental",
};

const PORTAL_SERVICES = {
  rentals: ["rental"],
  trucking: ["water", "bin", "diagnostics", "trucking"],
};

function StatusBadge({ status }) {
  return (
    <span className={`text-xs font-bold uppercase tracking-wider border rounded-md px-2 py-1 ${STATUS_COLORS[status] || ""}`}>
      {STATUS_LABELS[status] || status}
    </span>
  );
}
function ServiceIcon({ s }) {
  const I = SERVICE_ICONS[s] || ClipboardList;
  return <I className="h-4 w-4" strokeWidth={2.5} />;
}

function BookingDetail({ b }) {
  if (!b) return null;
  const entries = Object.entries(b).filter(([k]) =>
    !["_id", "customer_id", "id", "assigned_driver_id", "completion_photo", "signature_data"].includes(k));
  return (
    <div className="text-sm space-y-2 max-h-96 overflow-y-auto">
      {entries.map(([k, v]) => (
        <div key={k} className="grid grid-cols-3 gap-2 border-b border-slate-100 pb-1">
          <div className="text-slate-500 capitalize">{k.replaceAll("_", " ")}</div>
          <div className="col-span-2 font-medium break-words">{String(v ?? "—")}</div>
        </div>
      ))}
    </div>
  );
}

/* ---------- Customer (portal-aware) ---------- */
function CustomerView() {
  const { portal, setPortal } = usePortal();
  const [bookings, setBookings] = useState([]);
  const [leases, setLeases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(null);

  const load = async () => {
    try {
      const [b, l] = await Promise.all([
        api.get("/bookings/mine"),
        api.get("/leasing/mine").catch(() => ({ data: [] })),
      ]);
      setBookings(b.data);
      setLeases(l.data);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const allowed = portal ? PORTAL_SERVICES[portal] : Object.keys(SERVICE_LABELS);
  const filteredBookings = bookings.filter(b => allowed.includes(b.service));

  const quickTiles = portal === PORTAL_RENTALS
    ? [
        { key: "rental", to: "/rentals", label: "Vehicle Rentals", Ic: Car },
        { key: "lease", to: "/leasing", label: "Apply for Lease", Ic: FileText },
      ]
    : portal === PORTAL_TRUCKING
    ? [
        { key: "water", to: "/logistics/water", label: "Water Delivery", Ic: Droplets },
        { key: "bin", to: "/logistics/bin", label: "Bin Rental", Ic: Trash2 },
        { key: "diagnostics", to: "/logistics/diagnostics", label: "Diagnostics", Ic: Wrench },
        { key: "trucking", to: "/logistics/trucking", label: "Trucking", Ic: Truck },
      ]
    : [];

  return (
    <div className="space-y-6">
      {!portal && (
        <div className="bg-white border border-slate-200 rounded-md p-6 flex flex-col sm:flex-row gap-4 items-center justify-between">
          <div>
            <div className="tag-overline">Pick your portal</div>
            <div className="font-heading font-bold text-lg text-slate-900 mt-1">You haven't selected a portal yet.</div>
            <div className="text-sm text-slate-600">Choose which experience you want to see.</div>
          </div>
          <div className="flex gap-2">
            <Button data-testid="customer-pick-rentals" onClick={() => setPortal(PORTAL_RENTALS)} className="bg-[#E32636] hover:bg-[#C91F2C] text-white font-bold">
              <Car className="h-4 w-4 mr-1" /> Rentals & Leasing
            </Button>
            <Button data-testid="customer-pick-trucking" onClick={() => setPortal(PORTAL_TRUCKING)} variant="outline" className="font-bold">
              <Truck className="h-4 w-4 mr-1" /> Trucking & Industrial
            </Button>
          </div>
        </div>
      )}

      {portal && (
        <div className="flex items-center gap-2 text-sm">
          <span className="tag-overline">Active portal</span>
          <span className="font-bold text-slate-900">{PORTAL_LABELS[portal]}</span>
        </div>
      )}

      {quickTiles.length > 0 && (
        <div className={`grid grid-cols-2 gap-3 ${quickTiles.length > 2 ? "md:grid-cols-4" : "md:grid-cols-2"}`}>
          {quickTiles.map(({ key, to, label, Ic }) => (
            <Link key={key} to={to} className="bg-white border border-slate-200 rounded-md p-4 hover:border-slate-400 hover:-translate-y-0.5 transition-all" data-testid={`quick-${key}`}>
              <Ic className="h-5 w-5 text-[#E32636]" strokeWidth={2.5} />
              <div className="mt-2 font-bold text-slate-900 text-sm">{label}</div>
              <div className="text-xs text-slate-500 mt-1">Book now →</div>
            </Link>
          ))}
        </div>
      )}

      <div className="bg-white border border-slate-200 rounded-md">
        <div className="p-6 border-b border-slate-200 flex items-center justify-between">
          <div>
            <div className="tag-overline">Your bookings</div>
            <h3 className="font-heading font-black text-xl text-slate-900">Recent activity</h3>
          </div>
          <Button variant="outline" onClick={load} data-testid="refresh-bookings-btn">Refresh</Button>
        </div>
        {loading ? <div className="p-6 text-sm text-slate-500">Loading…</div> :
          filteredBookings.length === 0 ? <div className="p-8 text-center text-sm text-slate-500">No bookings in this portal yet.</div> :
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Service</TableHead><TableHead>Invoice</TableHead><TableHead>Date</TableHead><TableHead>Status</TableHead>
                <TableHead className="text-right">Amount</TableHead><TableHead></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredBookings.map((b) => (
                <TableRow key={b.id} data-testid={`booking-row-${b.id}`}>
                  <TableCell><div className="flex items-center gap-2"><ServiceIcon s={b.service} />{SERVICE_LABELS[b.service]}</div></TableCell>
                  <TableCell className="font-mono text-xs">{b.invoice_number}</TableCell>
                  <TableCell className="text-sm">{(b.created_at || "").slice(0, 10)}</TableCell>
                  <TableCell><StatusBadge status={b.status} /></TableCell>
                  <TableCell className="text-right font-bold">{b.service === "trucking" && !b.price ? "Pending quote" : formatXCD(b.price)}</TableCell>
                  <TableCell className="text-right space-x-1">
                    <Link to={`/invoice/${b.id}`}><Button variant="outline" size="sm" data-testid={`invoice-${b.id}`}>Invoice</Button></Link>
                    <Button variant="ghost" size="sm" onClick={() => setOpen(b)} data-testid={`view-booking-${b.id}`}>Details</Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>}
      </div>

      {portal === PORTAL_RENTALS && leases.length > 0 && (
        <div className="bg-white border border-slate-200 rounded-md">
          <div className="p-6 border-b border-slate-200">
            <div className="tag-overline">Your lease applications</div>
            <h3 className="font-heading font-black text-xl text-slate-900">Leasing</h3>
          </div>
          <div className="p-4 grid gap-3">
            {leases.map(l => (
              <div key={l.id} className="border border-slate-200 rounded-md p-4">
                <div className="flex items-center justify-between">
                  <div className="font-bold text-slate-900">{l.desired_vehicle}</div>
                  <span className="text-xs font-bold uppercase border rounded-md px-2 py-1 bg-slate-50">{l.status}</span>
                </div>
                <div className="text-xs text-slate-500 mt-1">{l.term_months} months · Budget EC${l.monthly_budget || 0}</div>
                {l.monthly_payment && <div className="text-xs text-emerald-700 mt-1">Approved at EC${l.monthly_payment}/mo</div>}
              </div>
            ))}
          </div>
        </div>
      )}

      <Dialog open={!!open} onOpenChange={(o) => !o && setOpen(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Booking {open?.invoice_number}</DialogTitle>
            <DialogDescription>Full details</DialogDescription>
          </DialogHeader>
          <BookingDetail b={open} />
        </DialogContent>
      </Dialog>
    </div>
  );
}

/* ---------- Driver ---------- */
function DriverView() {
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [active, setActive] = useState(null);
  const [note, setNote] = useState("");
  const [photoPath, setPhotoPath] = useState("");
  const [signature, setSignature] = useState("");

  const load = async () => {
    setLoading(true);
    try { const { data } = await api.get("/driver/jobs"); setJobs(data); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const updateStatus = async (id, status, extra = {}) => {
    try {
      await api.patch(`/driver/jobs/${id}`, { status, ...extra });
      toast.success(`Status: ${status}`);
      load();
      if (active && active.id === id) setActive(null);
    } catch { toast.error("Update failed"); }
  };

  const uploadPhoto = async (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    const fd = new FormData(); fd.append("file", f);
    try {
      const { data } = await api.post("/upload", fd);
      setPhotoPath(data.path);
      toast.success("Photo uploaded");
    } catch { toast.error("Upload failed"); }
  };

  return (
    <div className="space-y-6">
      <div className="bg-white border border-slate-200 rounded-md">
        <div className="p-6 border-b border-slate-200 flex items-center justify-between">
          <div>
            <div className="tag-overline">Assignments</div>
            <h3 className="font-heading font-black text-xl text-slate-900">Your jobs ({jobs.length})</h3>
          </div>
          <Button variant="outline" onClick={load} data-testid="driver-refresh-btn">Refresh</Button>
        </div>
        {loading ? <div className="p-6 text-sm text-slate-500">Loading…</div> :
          jobs.length === 0 ? <div className="p-8 text-center text-sm text-slate-500">No jobs assigned yet.</div> :
          <Table>
            <TableHeader><TableRow><TableHead>Job</TableHead><TableHead>Customer</TableHead><TableHead>Status</TableHead><TableHead></TableHead></TableRow></TableHeader>
            <TableBody>
              {jobs.map(j => (
                <TableRow key={j.id} data-testid={`driver-job-${j.id}`}>
                  <TableCell><div className="flex items-center gap-2"><ServiceIcon s={j.service} />{SERVICE_LABELS[j.service]} · {j.invoice_number}</div></TableCell>
                  <TableCell><div className="text-sm">{j.customer_name}<div className="text-xs text-slate-500">{j.customer_phone}</div></div></TableCell>
                  <TableCell><StatusBadge status={j.status} /></TableCell>
                  <TableCell className="text-right">
                    <Button size="sm" variant="outline" onClick={() => { setActive(j); setNote(""); setPhotoPath(""); setSignature(""); }} data-testid={`driver-open-${j.id}`}>Open</Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>}
      </div>

      <Dialog open={!!active} onOpenChange={(o) => !o && setActive(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Job · {active?.invoice_number}</DialogTitle>
            <DialogDescription>{SERVICE_LABELS[active?.service]} for {active?.customer_name}</DialogDescription>
          </DialogHeader>
          <BookingDetail b={active} />
          <div className="space-y-3 mt-2">
            <div className="grid grid-cols-2 gap-2">
              <Button onClick={() => updateStatus(active.id, "in_progress")} variant="outline" data-testid="driver-start-btn">Start job</Button>
              <Button onClick={() => updateStatus(active.id, "completed", { completion_photo: photoPath, signature_data: signature, notes: note })}
                      className="bg-emerald-600 hover:bg-emerald-700 text-white font-bold" data-testid="driver-complete-btn">
                <CheckCircle2 className="h-4 w-4 mr-1" /> Mark complete
              </Button>
            </div>
            <div>
              <Label className="text-sm font-bold">Photo</Label>
              <label className="mt-1 flex items-center gap-2 px-3 h-10 border-2 border-dashed border-slate-300 rounded-md cursor-pointer hover:border-[#1D4ED8] text-sm">
                <Camera className="h-4 w-4" />
                <input type="file" accept="image/*" onChange={uploadPhoto} className="hidden" data-testid="driver-photo-upload" />
                {photoPath ? "Uploaded ✓" : "Upload completion photo"}
              </label>
            </div>
            <div>
              <Label className="text-sm font-bold">Customer signature (typed)</Label>
              <Input data-testid="driver-signature" value={signature} onChange={e => setSignature(e.target.value)} placeholder="Customer signature" />
            </div>
            <div>
              <Label className="text-sm font-bold">Notes</Label>
              <Textarea data-testid="driver-notes" value={note} onChange={e => setNote(e.target.value)} />
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

/* ---------- Dispatcher ---------- */
function DispatcherView() {
  const [bookings, setBookings] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [filter, setFilter] = useState("all");
  const [assignTarget, setAssignTarget] = useState(null);
  const [driverId, setDriverId] = useState("");

  const load = async () => {
    const [b, d] = await Promise.all([api.get("/admin/bookings"), api.get("/admin/drivers")]);
    setBookings(b.data); setDrivers(d.data);
  };
  useEffect(() => { load(); }, []);

  const filtered = bookings.filter(b => filter === "all" || b.status === filter);

  const assign = async () => {
    if (!driverId) return toast.error("Pick a driver");
    try {
      await api.patch(`/admin/bookings/${assignTarget.id}/assign`, { driver_id: driverId });
      toast.success("Driver assigned");
      setAssignTarget(null); setDriverId(""); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const setStatus = async (id, status) => {
    try { await api.patch(`/admin/bookings/${id}/status`, { status }); toast.success(`Status: ${status}`); load(); }
    catch { toast.error("Failed"); }
  };

  const copyShareLink = async (b) => {
    try {
      const { data } = await api.get(`/admin/bookings/${b.id}/share-url`);
      await navigator.clipboard.writeText(data.share_url);
      toast.success("Invoice link copied");
    } catch { toast.error("Copy failed"); }
  };
  const whatsappShare = async (b) => {
    try {
      const { data } = await api.get(`/admin/bookings/${b.id}/share-url`);
      const msg = `Hi ${data.customer_name || ""}, here's your invoice ${data.invoice_number} from Cleavie's Enterprise — EC$${Number(data.price || 0).toFixed(2)}: ${data.share_url}`;
      const phone = (data.customer_phone || "").replace(/[^0-9]/g, "");
      const url = phone ? `https://wa.me/${phone}?text=${encodeURIComponent(msg)}` : `https://wa.me/?text=${encodeURIComponent(msg)}`;
      window.open(url, "_blank", "noopener");
    } catch { toast.error("Failed"); }
  };
  const emailSend = async (b) => {
    try {
      await api.post(`/admin/bookings/${b.id}/send-invoice`);
      toast.success("Invoice emailed to customer");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="tag-overline">Dispatch board</div>
        <Select value={filter} onValueChange={setFilter}>
          <SelectTrigger className="w-48" data-testid="dispatch-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All statuses</SelectItem>
            {Object.entries(STATUS_LABELS).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
      <div className="bg-white border border-slate-200 rounded-md overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow><TableHead>Service</TableHead><TableHead>Customer</TableHead><TableHead>Status</TableHead><TableHead>Driver</TableHead><TableHead className="text-right">Amount</TableHead><TableHead></TableHead></TableRow>
          </TableHeader>
          <TableBody>
            {filtered.map(b => (
              <TableRow key={b.id} data-testid={`dispatch-row-${b.id}`}>
                <TableCell><div className="flex items-center gap-2"><ServiceIcon s={b.service} />{SERVICE_LABELS[b.service]}</div><div className="text-xs text-slate-500 font-mono">{b.invoice_number}</div></TableCell>
                <TableCell><div className="text-sm">{b.customer_name}</div><div className="text-xs text-slate-500">{b.customer_phone}</div></TableCell>
                <TableCell><StatusBadge status={b.status} /></TableCell>
                <TableCell className="text-sm">{b.assigned_driver_name || <span className="text-slate-400">Unassigned</span>}</TableCell>
                <TableCell className="text-right font-bold">{formatXCD(b.price)}</TableCell>
                <TableCell className="text-right space-x-1">
                  <Button size="sm" variant="outline" onClick={() => { setAssignTarget(b); setDriverId(""); }} data-testid={`assign-btn-${b.id}`}>Assign</Button>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button size="sm" variant="outline" data-testid={`share-menu-${b.id}`} title="Share invoice">
                        <Share2 className="h-4 w-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="w-56">
                      <DropdownMenuItem onClick={() => window.open(`/invoice/${b.id}`, "_blank")} data-testid={`share-view-${b.id}`}>
                        <FileText className="h-4 w-4 mr-2" /> Open invoice
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => copyShareLink(b)} data-testid={`share-copy-${b.id}`}>
                        <Copy className="h-4 w-4 mr-2" /> Copy share link
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => whatsappShare(b)} data-testid={`share-wa-${b.id}`}>
                        <MessageCircle className="h-4 w-4 mr-2 text-emerald-600" /> WhatsApp customer
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => emailSend(b)} data-testid={`share-email-${b.id}`}>
                        <Mail className="h-4 w-4 mr-2" /> Email customer
                      </DropdownMenuItem>
                      {b.invoice_sent_at && (
                        <div className="text-[11px] text-slate-500 px-2 py-1 border-t mt-1">
                          Last sent {b.invoice_sent_at.slice(0, 16).replace("T", " ")}
                          {b.invoice_sent_count ? ` · ${b.invoice_sent_count}×` : ""}
                        </div>
                      )}
                    </DropdownMenuContent>
                  </DropdownMenu>
                  <Select value="" onValueChange={(v) => setStatus(b.id, v)}>
                    <SelectTrigger className="w-28 h-8 inline-flex" data-testid={`status-set-${b.id}`}><SelectValue placeholder="Status" /></SelectTrigger>
                    <SelectContent>{Object.entries(STATUS_LABELS).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}</SelectContent>
                  </Select>
                </TableCell>
              </TableRow>
            ))}
            {filtered.length === 0 && <TableRow><TableCell colSpan={6} className="text-center text-slate-500 py-8">No bookings</TableCell></TableRow>}
          </TableBody>
        </Table>
      </div>

      <Dialog open={!!assignTarget} onOpenChange={(o) => !o && setAssignTarget(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Assign driver</DialogTitle><DialogDescription>Job: {assignTarget?.invoice_number}</DialogDescription></DialogHeader>
          <Select value={driverId} onValueChange={setDriverId}>
            <SelectTrigger data-testid="assign-driver-select"><SelectValue placeholder="Pick a driver" /></SelectTrigger>
            <SelectContent>{drivers.map(d => <SelectItem key={d.id} value={d.id}>{d.name} ({d.email})</SelectItem>)}</SelectContent>
          </Select>
          <DialogFooter><Button onClick={assign} data-testid="assign-confirm-btn" className="bg-[#1D4ED8] hover:bg-[#1E40AF]">Assign</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

/* ---------- Admin tabs ---------- */
function AdminCreateBooking({ onCreated }) {
  const [open, setOpen] = useState(false);
  const [service, setService] = useState("water");
  const [customerEmail, setCustomerEmail] = useState("");
  const [customerName, setCustomerName] = useState("");
  const [customerPhone, setCustomerPhone] = useState("");
  const [createUser, setCreateUser] = useState(false);
  const [price, setPrice] = useState("");
  const [payload, setPayload] = useState({});
  const [busy, setBusy] = useState(false);
  const [vehicles, setVehicles] = useState([]);
  const [pricing, setPricing] = useState(null);

  useEffect(() => {
    if (!open) return;
    api.get("/admin/vehicles").then(r => setVehicles(r.data)).catch(() => {});
    api.get("/pricing").then(r => setPricing(r.data)).catch(() => {});
    setPayload({});
    setPrice("");
  }, [open, service]);

  // Auto-compute price for fixed-price services
  useEffect(() => {
    if (!pricing) return;
    if (service === "water" && payload.sku) {
      // Server-authoritative quote
      api.post("/pricing/water/quote", {
        sku: payload.sku,
        hill_area_id: payload.hill_area_id || null,
        delivery_time: payload.delivery_time || null,
        after_hours: typeof payload.after_hours === "boolean" ? payload.after_hours : undefined,
      }).then(r => setPrice(String(r.data.total))).catch(() => {});
    }
    if (service === "bin") setPrice(String(pricing.bin || ""));
    if (service === "diagnostics" && payload.vehicle_type) setPrice(String(pricing.diagnostics?.[payload.vehicle_type] || ""));
    if (service === "rental" && payload.vehicle_id && payload.pickup_date && payload.return_date) {
      const v = vehicles.find(x => x.id === payload.vehicle_id);
      if (v) {
        const days = Math.max(1, Math.ceil((new Date(payload.return_date) - new Date(payload.pickup_date)) / 86400000));
        setPrice(String(days * v.rate_per_day));
      }
    }
  }, [service, payload, pricing, vehicles]);

  const submit = async () => {
    if (!customerEmail) return toast.error("Customer email required");
    setBusy(true);
    try {
      // Create user if needed
      if (createUser) {
        try {
          await api.post("/admin/users", {
            email: customerEmail,
            name: customerName || "Walk-in customer",
            password: Math.random().toString(36).slice(2, 10) + "Aa1!",
            phone: customerPhone || "",
            roles: ["customer"],
          });
        } catch (e) {
          // ignore "already exists"
        }
      }
      const body = {
        service,
        customer_email: customerEmail,
        payload: { ...payload },
        price: price ? parseFloat(price) : undefined,
        status: "approved",
      };
      const { data } = await api.post("/admin/bookings", body);
      toast.success(`Created ${data.invoice_number}`);
      invalidateAll();
      setOpen(false);
      setCustomerEmail(""); setCustomerName(""); setCustomerPhone("");
      setPayload({}); setPrice("");
      onCreated && onCreated();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally { setBusy(false); }
  };

  const set = (k, v) => setPayload(p => ({ ...p, [k]: v }));

  return (
    <>
      <Button onClick={() => setOpen(true)} className="bg-[#E32636] hover:bg-[#C91F2C] text-white font-bold" data-testid="admin-new-booking-btn">
        <Plus className="h-4 w-4 mr-1" /> New booking (walk-in / phone)
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Create booking on behalf of customer</DialogTitle>
            <DialogDescription>For walk-ins, phone orders, or off-app bookings.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="text-sm font-bold">Service</Label>
              <Select value={service} onValueChange={setService}>
                <SelectTrigger data-testid="adm-svc"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="water">Water Delivery</SelectItem>
                  <SelectItem value="bin">Bin Rental</SelectItem>
                  <SelectItem value="diagnostics">Diagnostics</SelectItem>
                  <SelectItem value="trucking">Trucking (custom quote)</SelectItem>
                  <SelectItem value="rental">Vehicle Rental</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="border-t border-slate-200 pt-3">
              <Label className="text-sm font-bold">Customer email</Label>
              <Input data-testid="adm-cust-email" placeholder="customer@example.com" value={customerEmail} onChange={e => setCustomerEmail(e.target.value)} />
              <label className="flex items-center gap-2 text-xs mt-2 text-slate-600">
                <Checkbox checked={createUser} onCheckedChange={setCreateUser} data-testid="adm-create-user" />
                Create new customer account if email not found
              </label>
              {createUser && (
                <div className="grid grid-cols-2 gap-2 mt-2">
                  <div>
                    <Label className="text-xs">Name</Label>
                    <Input data-testid="adm-cust-name" value={customerName} onChange={e => setCustomerName(e.target.value)} />
                  </div>
                  <div>
                    <Label className="text-xs">Phone</Label>
                    <Input data-testid="adm-cust-phone" value={customerPhone} onChange={e => setCustomerPhone(e.target.value)} />
                  </div>
                </div>
              )}
            </div>

            {service === "water" && (
              <div className="grid grid-cols-2 gap-2">
                <div className="col-span-2">
                  <Label className="text-xs">Delivery size</Label>
                  <Select value={payload.sku || ""} onValueChange={(v) => set("sku", v)}>
                    <SelectTrigger data-testid="adm-water-sku"><SelectValue placeholder="Pick tier" /></SelectTrigger>
                    <SelectContent>
                      {(pricing?.water_tiers || []).filter(t => t.active !== false).map(t => (
                        <SelectItem key={t.sku} value={t.sku}>{t.label} — {formatXCD(t.price)}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="col-span-2">
                  <Label className="text-xs">Hill area (optional)</Label>
                  <Select value={payload.hill_area_id || "__none"} onValueChange={(v) => set("hill_area_id", v === "__none" ? "" : v)}>
                    <SelectTrigger data-testid="adm-water-hill"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__none">None</SelectItem>
                      {(pricing?.hill_areas || []).filter(a => a.active !== false).map(a => (
                        <SelectItem key={a.id} value={a.id}>{a.name} · +{formatXCD(a.surcharge)}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs">Date</Label>
                  <Input type="date" data-testid="adm-w-date" value={payload.delivery_date || ""} onChange={e => set("delivery_date", e.target.value)} />
                </div>
                <div>
                  <Label className="text-xs">Time</Label>
                  <Input type="time" data-testid="adm-w-time" value={payload.delivery_time || ""} onChange={e => set("delivery_time", e.target.value)} />
                </div>
                <div className="col-span-2 flex items-center gap-2">
                  <Checkbox
                    id="adm-w-ah"
                    checked={!!payload.after_hours}
                    onCheckedChange={(v) => set("after_hours", !!v)}
                    data-testid="adm-w-after-hours" />
                  <Label htmlFor="adm-w-ah" className="text-xs">
                    Force after-hours fee (otherwise auto-detected by time after {pricing?.after_hours?.start_time || "18:00"})
                  </Label>
                </div>
                <div className="col-span-2">
                  <Label className="text-xs">Address</Label>
                  <Input data-testid="adm-w-addr" value={payload.address || ""} onChange={e => set("address", e.target.value)} />
                </div>
              </div>
            )}

            {service === "bin" && (
              <div className="grid grid-cols-2 gap-2">
                <div className="col-span-2"><Label className="text-xs">Address</Label><Input data-testid="adm-b-addr" value={payload.address || ""} onChange={e => set("address", e.target.value)} /></div>
                <div><Label className="text-xs">Date</Label><Input type="date" data-testid="adm-b-date" value={payload.rental_date || ""} onChange={e => set("rental_date", e.target.value)} /></div>
                <div><Label className="text-xs">Duration (days)</Label><Input type="number" data-testid="adm-b-dur" value={payload.duration_days || 7} onChange={e => set("duration_days", parseInt(e.target.value || 1, 10))} /></div>
              </div>
            )}

            {service === "diagnostics" && (
              <div className="grid grid-cols-2 gap-2">
                <div><Label className="text-xs">Type</Label><Select value={payload.vehicle_type || ""} onValueChange={(v) => set("vehicle_type", v)}><SelectTrigger data-testid="adm-d-type"><SelectValue placeholder="Car/Truck" /></SelectTrigger><SelectContent><SelectItem value="car">Car</SelectItem><SelectItem value="truck">Truck</SelectItem></SelectContent></Select></div>
                <div><Label className="text-xs">Date</Label><Input type="date" data-testid="adm-d-date" value={payload.appointment_date || ""} onChange={e => set("appointment_date", e.target.value)} /></div>
                <div><Label className="text-xs">Make</Label><Input data-testid="adm-d-make" value={payload.vehicle_make || ""} onChange={e => set("vehicle_make", e.target.value)} /></div>
                <div><Label className="text-xs">Model</Label><Input data-testid="adm-d-model" value={payload.vehicle_model || ""} onChange={e => set("vehicle_model", e.target.value)} /></div>
              </div>
            )}

            {service === "trucking" && (
              <div className="grid grid-cols-2 gap-2">
                <div className="col-span-2"><Label className="text-xs">Material</Label><Input data-testid="adm-t-mat" placeholder="Aggregate, sand, …" value={payload.material_type || ""} onChange={e => set("material_type", e.target.value)} /></div>
                <div><Label className="text-xs">Quantity</Label><Input data-testid="adm-t-qty" value={payload.quantity || ""} onChange={e => set("quantity", e.target.value)} /></div>
                <div><Label className="text-xs">Date</Label><Input type="date" data-testid="adm-t-date" value={payload.date_required || ""} onChange={e => set("date_required", e.target.value)} /></div>
                <div><Label className="text-xs">Pickup</Label><Input data-testid="adm-t-pu" value={payload.pickup_location || ""} onChange={e => set("pickup_location", e.target.value)} /></div>
                <div><Label className="text-xs">Drop-off</Label><Input data-testid="adm-t-do" value={payload.delivery_location || ""} onChange={e => set("delivery_location", e.target.value)} /></div>
              </div>
            )}

            {service === "rental" && (
              <div className="grid grid-cols-2 gap-2">
                <div className="col-span-2"><Label className="text-xs">Vehicle</Label>
                  <Select value={payload.vehicle_id || ""} onValueChange={(v) => set("vehicle_id", v)}>
                    <SelectTrigger data-testid="adm-r-veh"><SelectValue placeholder="Pick" /></SelectTrigger>
                    <SelectContent>{vehicles.map(v => <SelectItem key={v.id} value={v.id}>{v.year} {v.make} {v.model} · {v.color}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div><Label className="text-xs">Pickup date</Label><Input type="date" data-testid="adm-r-pu" value={payload.pickup_date || ""} onChange={e => set("pickup_date", e.target.value)} /></div>
                <div><Label className="text-xs">Return date</Label><Input type="date" data-testid="adm-r-rt" value={payload.return_date || ""} onChange={e => set("return_date", e.target.value)} /></div>
                <div className="col-span-2"><Label className="text-xs">Driver name</Label><Input data-testid="adm-r-driver" value={payload.driver_name || ""} onChange={e => set("driver_name", e.target.value)} /></div>
                <div className="col-span-2"><Label className="text-xs">License #</Label><Input data-testid="adm-r-license" value={payload.driver_license_number || ""} onChange={e => set("driver_license_number", e.target.value)} /></div>
              </div>
            )}

            <div className="border-t border-slate-200 pt-3">
              <Label className="text-sm font-bold">Total (EC$)</Label>
              <Input type="number" data-testid="adm-price" value={price} onChange={e => setPrice(e.target.value)} placeholder="Auto-filled from pricing — override if needed" />
            </div>
          </div>
          <DialogFooter>
            <Button onClick={submit} disabled={busy} data-testid="adm-create-booking-btn" className="bg-[#1D4ED8] hover:bg-[#1E40AF]">
              {busy ? "Creating…" : "Create booking"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

function AdminBookings() {
  const [bookings, setBookings] = useState([]);
  const [quoteTarget, setQuoteTarget] = useState(null);
  const [amount, setAmount] = useState("");
  const load = async () => { const { data } = await api.get("/admin/bookings"); setBookings(data); };
  useEffect(() => { load(); }, []);

  const sendQuote = async () => {
    const a = parseFloat(amount); if (Number.isNaN(a) || a <= 0) return toast.error("Enter an amount");
    try { await api.patch(`/admin/bookings/${quoteTarget.id}/quote`, { amount: a }); toast.success("Quote sent"); setQuoteTarget(null); setAmount(""); load(); }
    catch { toast.error("Failed"); }
  };
  const cancelBooking = async (id) => {
    if (!confirm("Cancel this booking?")) return;
    await api.patch(`/admin/bookings/${id}`, { status: "cancelled" });
    toast.success("Cancelled"); load();
  };
  const setStatus = async (id, status) => {
    try { await api.patch(`/admin/bookings/${id}/status`, { status }); toast.success(`Status: ${status}`); load(); }
    catch { toast.error("Failed"); }
  };
  const copyShareLink = async (b) => {
    try {
      const { data } = await api.get(`/admin/bookings/${b.id}/share-url`);
      await navigator.clipboard.writeText(data.share_url);
      toast.success("Invoice link copied");
    } catch { toast.error("Copy failed"); }
  };
  const whatsappShare = async (b) => {
    try {
      const { data } = await api.get(`/admin/bookings/${b.id}/share-url`);
      const msg = `Hi ${data.customer_name || ""}, here's your invoice ${data.invoice_number} from Cleavie's Enterprise — EC$${Number(data.price || 0).toFixed(2)}: ${data.share_url}`;
      const phone = (data.customer_phone || "").replace(/[^0-9]/g, "");
      const url = phone ? `https://wa.me/${phone}?text=${encodeURIComponent(msg)}` : `https://wa.me/?text=${encodeURIComponent(msg)}`;
      window.open(url, "_blank", "noopener");
    } catch { toast.error("Failed"); }
  };
  const emailSend = async (b) => {
    try { await api.post(`/admin/bookings/${b.id}/send-invoice`); toast.success("Invoice emailed to customer"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const truckingPending = bookings.filter(b => b.service === "trucking" && !b.price);
  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="font-heading font-bold text-lg">All bookings ({bookings.length})</div>
        <AdminCreateBooking onCreated={load} />
      </div>
      {truckingPending.length > 0 && (
        <div className="bg-white border border-slate-200 rounded-md">
          <div className="p-5 border-b border-slate-200 flex items-center gap-2">
            <AlertCircle className="h-5 w-5 text-[#FFCE00]" />
            <div className="font-heading font-bold">Pending trucking quotes ({truckingPending.length})</div>
          </div>
          <Table>
            <TableHeader><TableRow><TableHead>Customer</TableHead><TableHead>Material</TableHead><TableHead>Route</TableHead><TableHead></TableHead></TableRow></TableHeader>
            <TableBody>
              {truckingPending.map(b => (
                <TableRow key={b.id}>
                  <TableCell>{b.customer_name}<div className="text-xs text-slate-500">{b.invoice_number}</div></TableCell>
                  <TableCell>{b.material_type} · {b.quantity}</TableCell>
                  <TableCell>{b.pickup_location} → {b.delivery_location}</TableCell>
                  <TableCell className="text-right">
                    <Button size="sm" onClick={() => { setQuoteTarget(b); setAmount(""); }} data-testid={`quote-btn-${b.id}`} className="bg-[#1D4ED8] hover:bg-[#1E40AF] text-white">Send quote</Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
      <div className="bg-white border border-slate-200 rounded-md">
        <Table>
          <TableHeader><TableRow><TableHead>Service</TableHead><TableHead>Customer</TableHead><TableHead>Status</TableHead><TableHead className="text-right">Amount</TableHead><TableHead></TableHead></TableRow></TableHeader>
          <TableBody>
            {bookings.map(b => (
              <TableRow key={b.id}>
                <TableCell><div className="flex items-center gap-2"><ServiceIcon s={b.service} />{SERVICE_LABELS[b.service]}</div><div className="text-xs text-slate-500 font-mono">{b.invoice_number}</div></TableCell>
                <TableCell>{b.customer_name}</TableCell>
                <TableCell><StatusBadge status={b.status} /></TableCell>
                <TableCell className="text-right font-bold">{formatXCD(b.price)}</TableCell>
                <TableCell className="text-right whitespace-nowrap space-x-1">
                  <Select value="" onValueChange={(v) => setStatus(b.id, v)}>
                    <SelectTrigger className="w-28 h-8 inline-flex" data-testid={`ab-status-${b.id}`}><SelectValue placeholder="Status" /></SelectTrigger>
                    <SelectContent>{Object.entries(STATUS_LABELS).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}</SelectContent>
                  </Select>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button size="sm" variant="outline" data-testid={`ab-share-${b.id}`} title="Invoice actions">
                        <Share2 className="h-4 w-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="w-56">
                      <DropdownMenuItem onClick={() => window.open(`/invoice/${b.id}`, "_blank")} data-testid={`ab-view-${b.id}`}>
                        <FileText className="h-4 w-4 mr-2" /> Open invoice
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => copyShareLink(b)} data-testid={`ab-copy-${b.id}`}>
                        <Copy className="h-4 w-4 mr-2" /> Copy share link
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => whatsappShare(b)} data-testid={`ab-wa-${b.id}`}>
                        <MessageCircle className="h-4 w-4 mr-2 text-emerald-600" /> WhatsApp customer
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => emailSend(b)} data-testid={`ab-email-${b.id}`}>
                        <Mail className="h-4 w-4 mr-2" /> Email customer
                      </DropdownMenuItem>
                      {b.invoice_sent_at && (
                        <div className="text-[11px] text-slate-500 px-2 py-1 border-t mt-1">
                          Last sent {b.invoice_sent_at.slice(0, 16).replace("T", " ")}
                          {b.invoice_sent_count ? ` · ${b.invoice_sent_count}×` : ""}
                        </div>
                      )}
                    </DropdownMenuContent>
                  </DropdownMenu>
                  <Button size="sm" variant="ghost" onClick={() => cancelBooking(b.id)} data-testid={`cancel-${b.id}`}><Trash className="h-4 w-4 text-rose-600" /></Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <Dialog open={!!quoteTarget} onOpenChange={(o) => !o && setQuoteTarget(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Send quote</DialogTitle><DialogDescription>{quoteTarget?.invoice_number} · {quoteTarget?.customer_name}</DialogDescription></DialogHeader>
          <div className="space-y-2">
            <Label className="text-sm font-bold">Amount (EC$)</Label>
            <Input data-testid="quote-amount-input" type="number" min="0" step="1" value={amount} onChange={(e) => setAmount(e.target.value)} />
          </div>
          <DialogFooter><Button onClick={sendQuote} className="bg-[#1D4ED8] hover:bg-[#1E40AF]" data-testid="quote-send-btn">Send</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function AdminLeases() {
  const [leases, setLeases] = useState([]);
  const [target, setTarget] = useState(null);
  const [status, setStatus] = useState("");
  const [payment, setPayment] = useState("");
  const [notes, setNotes] = useState("");

  const load = async () => { const { data } = await api.get("/admin/leases"); setLeases(data); };
  useEffect(() => { load(); }, []);

  const save = async () => {
    try {
      const body = { status, notes };
      if (payment) body.monthly_payment = parseFloat(payment);
      await api.patch(`/admin/leases/${target.id}`, body);
      toast.success("Updated"); setTarget(null); load();
    } catch { toast.error("Failed"); }
  };

  return (
    <div className="bg-white border border-slate-200 rounded-md">
      <div className="p-5 border-b border-slate-200 flex items-center gap-2"><FileText className="h-5 w-5 text-[#E32636]" /><div className="font-heading font-bold">Lease applications ({leases.length})</div></div>
      <Table>
        <TableHeader><TableRow><TableHead>Customer</TableHead><TableHead>Vehicle</TableHead><TableHead>Term</TableHead><TableHead>Budget</TableHead><TableHead>Status</TableHead><TableHead></TableHead></TableRow></TableHeader>
        <TableBody>
          {leases.map(l => (
            <TableRow key={l.id} data-testid={`lease-row-${l.id}`}>
              <TableCell>{l.customer_name}<div className="text-xs text-slate-500">{l.customer_email}</div></TableCell>
              <TableCell className="text-sm">{l.desired_vehicle}</TableCell>
              <TableCell className="text-sm">{l.term_months} mo</TableCell>
              <TableCell className="text-sm">EC${l.monthly_budget || 0}</TableCell>
              <TableCell><span className="text-xs font-bold uppercase border rounded-md px-2 py-1 bg-slate-50">{l.status}</span></TableCell>
              <TableCell className="text-right">
                <Button size="sm" variant="outline" data-testid={`lease-review-${l.id}`} onClick={() => { setTarget(l); setStatus(l.status); setPayment(l.monthly_payment || ""); setNotes(l.admin_notes || ""); }}>Review</Button>
              </TableCell>
            </TableRow>
          ))}
          {leases.length === 0 && <TableRow><TableCell colSpan={6} className="text-center text-slate-500 py-8">No lease applications yet.</TableCell></TableRow>}
        </TableBody>
      </Table>

      <Dialog open={!!target} onOpenChange={(o) => !o && setTarget(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Review lease</DialogTitle><DialogDescription>{target?.customer_name} · {target?.desired_vehicle}</DialogDescription></DialogHeader>
          <BookingDetail b={target} />
          <div className="space-y-3 mt-2">
            <div>
              <Label className="text-sm font-bold">Status</Label>
              <Select value={status} onValueChange={setStatus}>
                <SelectTrigger data-testid="lease-status-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {["submitted", "under_review", "approved", "rejected", "active", "completed"].map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-sm font-bold">Monthly payment (if approved)</Label>
              <Input data-testid="lease-monthly-payment" type="number" value={payment} onChange={e => setPayment(e.target.value)} />
            </div>
            <div>
              <Label className="text-sm font-bold">Admin notes</Label>
              <Textarea data-testid="lease-admin-notes" value={notes} onChange={e => setNotes(e.target.value)} />
            </div>
          </div>
          <DialogFooter><Button onClick={save} data-testid="lease-save-btn" className="bg-[#1D4ED8] hover:bg-[#1E40AF]">Save</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function AdminPricing() {
  const [p, setP] = useState(null);
  const [reason, setReason] = useState("");
  const [history, setHistory] = useState([]);
  const [showHistory, setShowHistory] = useState(false);

  const load = async () => {
    const { data } = await api.get("/pricing");
    // Deep-clone so edits don't mutate original
    setP(JSON.parse(JSON.stringify(data)));
  };
  const loadHistory = async () => {
    try { const { data } = await api.get("/admin/pricing-history"); setHistory(data); } catch { /* noop */ }
  };
  useEffect(() => { load(); }, []);
  if (!p) return <div className="text-sm text-slate-500">Loading pricing…</div>;

  const updateTier = (idx, patch) => {
    const tiers = [...(p.water_tiers || [])];
    tiers[idx] = { ...tiers[idx], ...patch };
    setP({ ...p, water_tiers: tiers });
  };
  const updateArea = (idx, patch) => {
    const areas = [...(p.hill_areas || [])];
    areas[idx] = { ...areas[idx], ...patch };
    setP({ ...p, hill_areas: areas });
  };
  const addArea = () => {
    const areas = [...(p.hill_areas || [])];
    areas.push({ id: "", name: "New area", description: "", surcharge: 100, active: true });
    setP({ ...p, hill_areas: areas });
  };
  const removeArea = (idx) => {
    const areas = [...(p.hill_areas || [])];
    areas.splice(idx, 1);
    setP({ ...p, hill_areas: areas });
  };

  const save = async () => {
    try {
      await api.put("/admin/pricing", {
        water_tiers: (p.water_tiers || []).map(t => ({
          sku: t.sku, label: t.label, price: parseFloat(t.price || 0), active: !!t.active,
        })),
        hill_areas: (p.hill_areas || []).map(a => ({
          id: a.id || undefined, name: a.name, description: a.description || "",
          surcharge: parseFloat(a.surcharge || 0), active: !!a.active,
        })),
        after_hours: {
          fee: parseFloat(p.after_hours?.fee || 0),
          start_time: p.after_hours?.start_time || "18:00",
          active: p.after_hours?.active !== false,
        },
        bin: parseFloat(p.bin),
        diagnostics: p.diagnostics,
        rental_per_day: parseFloat(p.rental_per_day),
        reason,
      });
      toast.success("Pricing updated · live on site"); invalidateAll(); setReason(""); load();
    } catch { toast.error("Failed"); }
  };

  return (
    <div className="bg-white border border-slate-200 rounded-md p-6 max-w-4xl">
      <div className="flex items-center justify-between gap-4">
        <div className="flex items-center gap-2"><DollarSign className="h-5 w-5 text-emerald-600" /><div className="font-heading font-bold">Pricing (EC$)</div></div>
        <Button variant="outline" size="sm" onClick={() => { if (!showHistory) loadHistory(); setShowHistory(s => !s); }} data-testid="pricing-history-toggle">
          {showHistory ? "Hide history" : "Show price history"}
        </Button>
      </div>

      <div className="mt-4 space-y-6">
        {/* Water tiers */}
        <div>
          <Label className="text-sm font-bold flex items-center gap-2"><Droplets className="h-4 w-4 text-blue-600" /> Bulk water delivery — Cleve's Enterprises tiers</Label>
          <div className="mt-2 space-y-2">
            {(p.water_tiers || []).map((t, i) => (
              <div key={t.sku} className="grid grid-cols-12 gap-2 items-center border border-slate-200 rounded p-2" data-testid={`water-tier-row-${t.sku}`}>
                <div className="col-span-1 text-xs text-slate-400 font-mono">{t.sku}</div>
                <Input
                  className="col-span-6" placeholder="Label"
                  value={t.label || ""} onChange={e => updateTier(i, { label: e.target.value })}
                  data-testid={`tier-label-${t.sku}`}
                />
                <div className="col-span-3">
                  <Input type="number" step="0.01" value={t.price}
                         onChange={e => updateTier(i, { price: e.target.value })}
                         data-testid={`tier-price-${t.sku}`} />
                </div>
                <label className="col-span-2 flex items-center gap-1 text-xs">
                  <Checkbox checked={!!t.active} onCheckedChange={(v) => updateTier(i, { active: !!v })} data-testid={`tier-active-${t.sku}`} />
                  Active
                </label>
              </div>
            ))}
          </div>
        </div>

        {/* Hill Areas */}
        <div>
          <div className="flex items-center justify-between">
            <Label className="text-sm font-bold">Hill surcharge areas</Label>
            <Button size="sm" variant="outline" onClick={addArea} data-testid="hill-add-btn">
              <Plus className="h-3 w-3 mr-1" /> Add area
            </Button>
          </div>
          <div className="mt-2 space-y-2">
            {(p.hill_areas || []).map((a, i) => (
              <div key={i} className="grid grid-cols-12 gap-2 items-center border border-slate-200 rounded p-2" data-testid={`hill-row-${i}`}>
                <Input className="col-span-3" placeholder="Name" value={a.name || ""} onChange={e => updateArea(i, { name: e.target.value })} data-testid={`hill-name-${i}`} />
                <Input className="col-span-5" placeholder="Description" value={a.description || ""} onChange={e => updateArea(i, { description: e.target.value })} />
                <Input className="col-span-2" type="number" step="0.01" placeholder="Surcharge" value={a.surcharge} onChange={e => updateArea(i, { surcharge: e.target.value })} data-testid={`hill-surcharge-${i}`} />
                <label className="col-span-1 flex items-center gap-1 text-xs">
                  <Checkbox checked={!!a.active} onCheckedChange={(v) => updateArea(i, { active: !!v })} data-testid={`hill-active-${i}`} />
                  On
                </label>
                <Button size="icon" variant="ghost" onClick={() => removeArea(i)} data-testid={`hill-del-${i}`}>
                  <Trash className="h-3 w-3 text-slate-400" />
                </Button>
              </div>
            ))}
          </div>
        </div>

        {/* After-hours */}
        <div>
          <Label className="text-sm font-bold">After-hours fee</Label>
          <div className="grid grid-cols-3 gap-2 mt-1">
            <div>
              <Label className="text-xs text-slate-500">Fee (EC$)</Label>
              <Input type="number" step="0.01" value={p.after_hours?.fee ?? 50}
                     onChange={e => setP({ ...p, after_hours: { ...(p.after_hours || {}), fee: e.target.value } })}
                     data-testid="ah-fee" />
            </div>
            <div>
              <Label className="text-xs text-slate-500">Starts at</Label>
              <Input type="time" value={p.after_hours?.start_time || "18:00"}
                     onChange={e => setP({ ...p, after_hours: { ...(p.after_hours || {}), start_time: e.target.value } })}
                     data-testid="ah-start" />
            </div>
            <label className="flex items-end gap-1 text-xs mb-2">
              <Checkbox checked={p.after_hours?.active !== false}
                        onCheckedChange={(v) => setP({ ...p, after_hours: { ...(p.after_hours || {}), active: !!v } })}
                        data-testid="ah-active" />
              Active
            </label>
          </div>
        </div>

        {/* Other services */}
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label className="text-sm font-bold">Bin rental</Label>
            <Input data-testid="price-bin" type="number" value={p.bin} onChange={e => setP({ ...p, bin: e.target.value })} />
          </div>
          <div>
            <Label className="text-sm font-bold">Rental per day</Label>
            <Input data-testid="price-rental" type="number" value={p.rental_per_day} onChange={e => setP({ ...p, rental_per_day: e.target.value })} />
          </div>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label className="text-sm font-bold">Diagnostics — Car</Label>
            <Input data-testid="price-diag-car" type="number" value={p.diagnostics.car} onChange={e => setP({ ...p, diagnostics: { ...p.diagnostics, car: parseFloat(e.target.value || 0) } })} />
          </div>
          <div>
            <Label className="text-sm font-bold">Diagnostics — Truck</Label>
            <Input data-testid="price-diag-truck" type="number" value={p.diagnostics.truck} onChange={e => setP({ ...p, diagnostics: { ...p.diagnostics, truck: parseFloat(e.target.value || 0) } })} />
          </div>
        </div>

        <div>
          <Label className="text-sm font-bold">Change reason (optional, saved to history)</Label>
          <Input value={reason} onChange={e => setReason(e.target.value)} placeholder="e.g. Fuel cost adjustment 2026-Q1" data-testid="pricing-reason" />
        </div>

        <Button onClick={save} data-testid="pricing-save-btn" className="bg-[#1D4ED8] hover:bg-[#1E40AF] text-white font-bold">
          <Save className="h-4 w-4 mr-1" />Save pricing
        </Button>

        {showHistory && (
          <div className="mt-4 border-t border-slate-200 pt-4">
            <div className="text-sm font-bold mb-2">Price history</div>
            <div className="max-h-80 overflow-y-auto text-xs">
              <table className="w-full">
                <thead className="text-left text-slate-500">
                  <tr>
                    <th className="py-1">When</th>
                    <th>Category</th>
                    <th>SKU</th>
                    <th>Prev</th>
                    <th>New</th>
                    <th>Admin</th>
                    <th>Reason</th>
                  </tr>
                </thead>
                <tbody>
                  {history.length === 0 && (
                    <tr><td colSpan={7} className="py-2 text-slate-400">No history yet.</td></tr>
                  )}
                  {history.map(h => (
                    <tr key={h.id} className="border-t border-slate-100">
                      <td className="py-1">{(h.changed_at || "").slice(0, 16).replace("T", " ")}</td>
                      <td>{h.category}</td>
                      <td className="font-mono">{h.sku}</td>
                      <td>{h.previous_price ?? "—"}</td>
                      <td>{h.new_price ?? "—"}</td>
                      <td>{h.admin_email}</td>
                      <td className="text-slate-500">{h.reason || ""}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function AdminVehicles() {
  const [list, setList] = useState([]);
  const [editing, setEditing] = useState(null);
  const empty = { make: "", model: "", year: new Date().getFullYear(), color: "", rate_per_day: 130, transmission: "Automatic", features: [], image_url: "", active: true };

  const load = async () => { const { data } = await api.get("/admin/vehicles"); setList(data); };
  useEffect(() => { load(); }, []);

  const save = async () => {
    try {
      if (editing.id) await api.put(`/admin/vehicles/${editing.id}`, editing);
      else await api.post("/admin/vehicles", editing);
      toast.success("Saved · live on site"); invalidateAll(); setEditing(null); load();
    } catch { toast.error("Failed"); }
  };

  const remove = async (id) => {
    if (!confirm("Delete this vehicle?")) return;
    await api.delete(`/admin/vehicles/${id}`);
    toast.success("Deleted · live on site"); invalidateAll(); load();
  };

  const uploadImg = async (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    const fd = new FormData(); fd.append("file", f);
    try {
      const { data } = await api.post("/upload", fd);
      const url = `${process.env.REACT_APP_BACKEND_URL}/api/files/${data.path}`;
      setEditing(s => ({ ...s, image_url: url }));
      toast.success("Image uploaded");
    } catch { toast.error("Upload failed"); }
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <div className="font-heading font-bold text-lg">Fleet</div>
        <Button onClick={() => setEditing(empty)} data-testid="vehicle-new-btn" className="bg-[#E32636] hover:bg-[#C91F2C] text-white font-bold"><Plus className="h-4 w-4 mr-1" />Add vehicle</Button>
      </div>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {list.map(v => (
          <div key={v.id} className="bg-white border border-slate-200 rounded-md overflow-hidden" data-testid={`vehicle-card-${v.id}`}>
            <div className="aspect-[16/10] bg-slate-50 flex items-center justify-center p-4">
              {v.image_url ? <img src={v.image_url} alt="" className="max-h-full object-contain" /> : <Car className="h-10 w-10 text-slate-300" />}
            </div>
            <div className="p-4 border-t border-slate-200">
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-xs uppercase font-bold tracking-wider text-[#1D4ED8]">{v.color}</div>
                  <div className="font-heading font-bold">{v.year} {v.make} {v.model}</div>
                </div>
                <div className="text-right"><div className="font-heading font-black text-lg text-[#E32636]">EC${v.rate_per_day}</div><div className="text-[10px] text-slate-500">per day</div></div>
              </div>
              <div className="flex gap-2 mt-3">
                <Button size="sm" variant="outline" onClick={() => setEditing(v)} data-testid={`vehicle-edit-${v.id}`}><Edit className="h-3 w-3 mr-1" />Edit</Button>
                <Button size="sm" variant="ghost" onClick={() => remove(v.id)} data-testid={`vehicle-del-${v.id}`}><Trash className="h-3 w-3 text-rose-600" /></Button>
              </div>
            </div>
          </div>
        ))}
      </div>

      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>{editing?.id ? "Edit vehicle" : "Add vehicle"}</DialogTitle></DialogHeader>
          {editing && (
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-2">
                <div><Label className="text-sm font-bold">Make</Label><Input data-testid="veh-make" value={editing.make} onChange={e => setEditing({ ...editing, make: e.target.value })} /></div>
                <div><Label className="text-sm font-bold">Model</Label><Input data-testid="veh-model" value={editing.model} onChange={e => setEditing({ ...editing, model: e.target.value })} /></div>
              </div>
              <div className="grid grid-cols-3 gap-2">
                <div><Label className="text-sm font-bold">Year</Label><Input data-testid="veh-year" type="number" value={editing.year} onChange={e => setEditing({ ...editing, year: parseInt(e.target.value || 0, 10) })} /></div>
                <div><Label className="text-sm font-bold">Color</Label><Input data-testid="veh-color" value={editing.color} onChange={e => setEditing({ ...editing, color: e.target.value })} /></div>
                <div><Label className="text-sm font-bold">EC$/day</Label><Input data-testid="veh-rate" type="number" value={editing.rate_per_day} onChange={e => setEditing({ ...editing, rate_per_day: parseFloat(e.target.value || 0) })} /></div>
              </div>
              <div>
                <Label className="text-sm font-bold">Image URL</Label>
                <Input data-testid="veh-image" value={editing.image_url} onChange={e => setEditing({ ...editing, image_url: e.target.value })} />
                <label className="mt-2 flex items-center gap-2 px-3 h-9 border-2 border-dashed border-slate-300 rounded-md cursor-pointer text-sm hover:border-[#1D4ED8]">
                  <Camera className="h-4 w-4" /> Upload image
                  <input type="file" accept="image/*" onChange={uploadImg} className="hidden" data-testid="veh-image-upload" />
                </label>
              </div>
              <div>
                <Label className="text-sm font-bold">Features (comma-separated)</Label>
                <Input data-testid="veh-features" value={(editing.features || []).join(", ")} onChange={e => setEditing({ ...editing, features: e.target.value.split(",").map(s => s.trim()).filter(Boolean) })} />
              </div>
            </div>
          )}
          <DialogFooter><Button onClick={save} data-testid="veh-save-btn" className="bg-[#1D4ED8] hover:bg-[#1E40AF]"><Save className="h-4 w-4 mr-1" />Save</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function AdminStaff() {
  const [users, setUsers] = useState([]);
  const [creating, setCreating] = useState(false);
  const [resetting, setResetting] = useState(null); // user being reset
  const [resetPw, setResetPw] = useState("");
  const [resetResult, setResetResult] = useState(null); // {email, new_password}
  const empty = { email: "", name: "", password: "", phone: "", roles: ["customer"] };
  const [form, setForm] = useState(empty);

  const load = async () => { const { data } = await api.get("/admin/users"); setUsers(data); };
  useEffect(() => { load(); }, []);

  const toggleRole = async (u, role) => {
    const next = (u.roles || []).includes(role) ? u.roles.filter(r => r !== role) : [...u.roles, role];
    if (next.length === 0) return toast.error("User must have at least one role");
    try { await api.patch(`/admin/users/${u.id}/roles`, { roles: next }); toast.success("Roles updated"); load(); }
    catch { toast.error("Failed"); }
  };

  const create = async () => {
    try {
      await api.post("/admin/users", form);
      toast.success("Created"); setCreating(false); setForm(empty); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const remove = async (id) => {
    if (!confirm("Delete this user?")) return;
    try { await api.delete(`/admin/users/${id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const openReset = (u) => {
    // Suggest a readable temporary password
    const temp = `Cleavies${Math.floor(1000 + Math.random() * 9000)}`;
    setResetPw(temp);
    setResetResult(null);
    setResetting(u);
  };

  const doReset = async () => {
    if (!resetPw || resetPw.length < 6) return toast.error("Password must be at least 6 characters");
    try {
      const { data } = await api.post(`/admin/users/${resetting.id}/reset-password`, { new_password: resetPw });
      setResetResult(data);
      toast.success("Password reset — share with the customer");
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="font-heading font-bold text-lg">Staff & customers ({users.length})</div>
        <Button onClick={() => setCreating(true)} data-testid="staff-new-btn" className="bg-[#E32636] hover:bg-[#C91F2C] text-white font-bold"><Plus className="h-4 w-4 mr-1" />Add user</Button>
      </div>
      <div className="bg-white border border-slate-200 rounded-md overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow><TableHead>User</TableHead><TableHead>Customer</TableHead><TableHead>Driver</TableHead><TableHead>Dispatcher</TableHead><TableHead>Admin</TableHead><TableHead></TableHead></TableRow>
          </TableHeader>
          <TableBody>
            {users.map(u => (
              <TableRow key={u.id}>
                <TableCell><div className="font-bold text-sm">{u.name}</div><div className="text-xs text-slate-500">{u.email}</div></TableCell>
                {["customer", "driver", "dispatcher", "admin"].map(r => (
                  <TableCell key={r}><Checkbox checked={(u.roles || []).includes(r)} onCheckedChange={() => toggleRole(u, r)} data-testid={`role-${r}-${u.id}`} /></TableCell>
                ))}
                <TableCell className="text-right whitespace-nowrap">
                  <Button size="sm" variant="ghost" onClick={() => openReset(u)} title="Reset password"
                          data-testid={`user-reset-${u.id}`}>
                    <KeyRound className="h-4 w-4 text-slate-600" />
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => remove(u.id)} data-testid={`user-del-${u.id}`}>
                    <Trash className="h-4 w-4 text-rose-600" />
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <Dialog open={creating} onOpenChange={(o) => !o && setCreating(false)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Create user</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label className="text-sm font-bold">Name</Label><Input data-testid="staff-name" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} /></div>
            <div><Label className="text-sm font-bold">Email</Label><Input data-testid="staff-email" type="email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} /></div>
            <div><Label className="text-sm font-bold">Phone</Label><Input data-testid="staff-phone" value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })} /></div>
            <div><Label className="text-sm font-bold">Password</Label><Input data-testid="staff-password" type="password" value={form.password} onChange={e => setForm({ ...form, password: e.target.value })} /></div>
            <div>
              <Label className="text-sm font-bold">Roles</Label>
              <div className="grid grid-cols-2 gap-2 mt-1">
                {["customer", "driver", "dispatcher", "admin"].map(r => (
                  <label key={r} className="flex items-center gap-2 text-sm">
                    <Checkbox data-testid={`staff-role-${r}`} checked={form.roles.includes(r)} onCheckedChange={(c) => setForm({ ...form, roles: c ? [...form.roles, r] : form.roles.filter(x => x !== r) })} />
                    {r}
                  </label>
                ))}
              </div>
            </div>
          </div>
          <DialogFooter><Button onClick={create} data-testid="staff-create-btn" className="bg-[#1D4ED8] hover:bg-[#1E40AF]">Create</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Admin-triggered password reset dialog */}
      <Dialog open={!!resetting} onOpenChange={(o) => { if (!o) { setResetting(null); setResetResult(null); setResetPw(""); } }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Reset password · {resetting?.name}</DialogTitle>
          </DialogHeader>
          {resetResult ? (
            <div className="space-y-3" data-testid="reset-success">
              <div className="bg-emerald-50 border border-emerald-200 text-emerald-900 rounded-md p-3 text-sm">
                Password reset. Read this to <strong>{resetResult.email}</strong>:
              </div>
              <div className="bg-slate-900 text-white font-mono rounded-md p-4 text-center text-xl tracking-wide" data-testid="reset-new-password">
                {resetResult.new_password}
              </div>
              <div className="text-xs text-slate-500">
                We also emailed it to the customer. Ask them to change it after signing in.
              </div>
              <DialogFooter>
                <Button onClick={() => { setResetting(null); setResetResult(null); setResetPw(""); }}>Done</Button>
              </DialogFooter>
            </div>
          ) : (
            <div className="space-y-3">
              <div className="text-sm text-slate-600">
                Set a temporary password for <strong>{resetting?.email}</strong>. The customer can sign in with it, then change it from their account.
              </div>
              <div>
                <Label className="text-sm font-bold">New password</Label>
                <Input data-testid="admin-reset-password-input" value={resetPw} onChange={e => setResetPw(e.target.value)} />
              </div>
              <DialogFooter>
                <Button onClick={doReset} data-testid="admin-reset-password-btn" className="bg-[#E32636] hover:bg-[#C91F2C] text-white font-bold">
                  Reset password
                </Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function AdminContent() {
  const [c, setC] = useState(null);
  const load = async () => { const { data } = await api.get("/content"); setC(data); };
  useEffect(() => { load(); }, []);
  if (!c) return <div className="text-sm text-slate-500">Loading…</div>;

  const save = async () => {
    try { await api.put("/admin/content", c); toast.success("Content saved · live on site"); invalidateAll(); load(); }
    catch { toast.error("Failed"); }
  };

  const fields = [
    ["hero_kicker", "Hero kicker"],
    ["hero_title", "Hero title"],
    ["hero_subtitle", "Hero subtitle"],
    ["rentals_tagline", "Rentals portal tagline"],
    ["trucking_tagline", "Trucking portal tagline"],
    ["about_text", "About text"],
    ["phone", "Contact phone"],
    ["email", "Contact email"],
    ["address", "Address"],
    ["announcement", "Announcement banner (optional)"],
  ];
  return (
    <div className="bg-white border border-slate-200 rounded-md p-6 max-w-3xl">
      <div className="flex items-center gap-2"><Settings className="h-5 w-5 text-[#1D4ED8]" /><div className="font-heading font-bold">Website content</div></div>
      <div className="mt-4 space-y-3">
        {fields.map(([k, label]) => (
          <div key={k}>
            <Label className="text-sm font-bold">{label}</Label>
            {k === "hero_subtitle" || k === "about_text" ? (
              <Textarea data-testid={`cms-${k}`} rows={3} value={c[k] || ""} onChange={e => setC({ ...c, [k]: e.target.value })} />
            ) : (
              <Input data-testid={`cms-${k}`} value={c[k] || ""} onChange={e => setC({ ...c, [k]: e.target.value })} />
            )}
          </div>
        ))}
        <Button onClick={save} data-testid="cms-save-btn" className="bg-[#1D4ED8] hover:bg-[#1E40AF] text-white font-bold"><Save className="h-4 w-4 mr-1" />Save content</Button>
      </div>
    </div>
  );
}

function AdminReports() {
  const [s, setS] = useState(null);
  useEffect(() => { api.get("/admin/reports/summary").then(r => setS(r.data)); }, []);
  if (!s) return <div className="text-sm text-slate-500">Loading…</div>;
  return (
    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
      <div className="bg-white border border-slate-200 rounded-md p-4">
        <div className="text-xs uppercase font-bold tracking-wider text-slate-500">Customers</div>
        <div className="font-heading font-black text-3xl text-slate-900 mt-1">{s.total_customers}</div>
      </div>
      {s.by_service.map(x => (
        <div key={x._id} className="bg-white border border-slate-200 rounded-md p-4">
          <div className="text-xs uppercase font-bold tracking-wider text-slate-500">{SERVICE_LABELS[x._id] || x._id}</div>
          <div className="font-heading font-black text-3xl text-slate-900 mt-1">{x.count}</div>
          <div className="text-xs text-emerald-700 font-bold">{formatXCD(x.revenue)}</div>
        </div>
      ))}
    </div>
  );
}

function AdminFactoryReset() {
  const [busy, setBusy] = useState(false);
  const [confirmFull, setConfirmFull] = useState(false);
  const [result, setResult] = useState(null);

  const run = async (mode) => {
    if (mode === "full" && !confirmFull) {
      setConfirmFull(true);
      return;
    }
    if (!confirm(mode === "full"
      ? "FULL WIPE: delete ALL customers, drivers, bookings, leases, notifications. Only admin accounts will remain. Continue?"
      : "Delete all bookings, leases, notifications, files, sessions, and non-admin users created MORE than 24 hours ago. Continue?")) {
      setConfirmFull(false);
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post("/admin/factory-reset", { mode });
      setResult(data);
      toast.success("Reset complete");
      invalidateAll();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally { setBusy(false); setConfirmFull(false); }
  };

  return (
    <div className="bg-white border border-rose-200 rounded-md p-6 max-w-2xl">
      <div className="flex items-center gap-2 text-rose-700"><AlertTriangle className="h-5 w-5" /><div className="font-heading font-bold">Danger zone — data reset</div></div>
      <p className="text-sm text-slate-600 mt-2">
        These actions are destructive. Admin accounts are <b>always preserved</b>. Vehicles, pricing and site content stay intact.
      </p>

      <div className="mt-5 space-y-4">
        <div className="border border-slate-200 rounded-md p-4">
          <div className="flex items-start justify-between gap-3">
            <div>
              <div className="font-bold text-slate-900 text-sm">Clear data older than 24 hours</div>
              <div className="text-xs text-slate-600 mt-0.5">Removes test bookings/leases/notifications and non-admin users created before 24h ago.</div>
            </div>
            <Button onClick={() => run("older_than_24h")} disabled={busy} variant="outline"
                    data-testid="reset-24h-btn" className="font-bold whitespace-nowrap">
              <RefreshCw className="h-4 w-4 mr-1" /> Run cleanup
            </Button>
          </div>
        </div>

        <div className="border-2 border-rose-300 bg-rose-50 rounded-md p-4">
          <div className="flex items-start justify-between gap-3">
            <div>
              <div className="font-bold text-rose-900 text-sm">Full factory reset</div>
              <div className="text-xs text-rose-800 mt-0.5">Wipes ALL customers, drivers, bookings, leases, notifications. Use only when you want a fresh production start.</div>
            </div>
            <Button onClick={() => run("full")} disabled={busy}
                    data-testid="reset-full-btn" className="bg-rose-600 hover:bg-rose-700 text-white font-bold whitespace-nowrap">
              <Trash className="h-4 w-4 mr-1" /> {confirmFull ? "Confirm wipe" : "Full wipe"}
            </Button>
          </div>
        </div>

        {result && (
          <div className="border border-emerald-200 bg-emerald-50 rounded-md p-4 text-sm">
            <div className="font-bold text-emerald-900">Done · {result.mode}</div>
            <pre className="text-xs text-emerald-800 mt-1 font-mono">{JSON.stringify(result.deleted, null, 2)}</pre>
          </div>
        )}
      </div>
    </div>
  );
}

function AdminView() {
  return (
    <Tabs defaultValue="reports" className="space-y-4">
      <TabsList className="bg-white border border-slate-200 flex-wrap h-auto">
        <TabsTrigger value="reports" data-testid="admin-tab-reports">Reports</TabsTrigger>
        <TabsTrigger value="bookings" data-testid="admin-tab-bookings">Bookings</TabsTrigger>
        <TabsTrigger value="leases" data-testid="admin-tab-leases">Leases</TabsTrigger>
        <TabsTrigger value="pricing" data-testid="admin-tab-pricing">Pricing</TabsTrigger>
        <TabsTrigger value="vehicles" data-testid="admin-tab-vehicles">Vehicles</TabsTrigger>
        <TabsTrigger value="staff" data-testid="admin-tab-staff">Staff</TabsTrigger>
        <TabsTrigger value="content" data-testid="admin-tab-content">Content</TabsTrigger>
        <TabsTrigger value="settings" data-testid="admin-tab-settings">Settings</TabsTrigger>
      </TabsList>
      <TabsContent value="reports"><AdminReports /></TabsContent>
      <TabsContent value="bookings"><AdminBookings /></TabsContent>
      <TabsContent value="leases"><AdminLeases /></TabsContent>
      <TabsContent value="pricing"><AdminPricing /></TabsContent>
      <TabsContent value="vehicles"><AdminVehicles /></TabsContent>
      <TabsContent value="staff"><AdminStaff /></TabsContent>
      <TabsContent value="content"><AdminContent /></TabsContent>
      <TabsContent value="settings"><AdminFactoryReset /></TabsContent>
    </Tabs>
  );
}

/* ---------- Main ---------- */
export default function DashboardPage() {
  const { user, loading } = useAuth();
  const nav = useNavigate();
  useEffect(() => { if (!loading && !user) nav("/login"); }, [loading, user, nav]);
  if (loading) return <Layout><div className="p-12 text-center text-slate-500">Loading…</div></Layout>;
  if (!user) return null;

  const role = user.active_role || (user.roles && user.roles[0]) || "customer";

  return (
    <Layout>
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex flex-wrap items-end justify-between gap-4 mb-6">
          <div>
            <div className="tag-overline">{ROLE_LABELS[role]} dashboard</div>
            <h1 className="font-heading font-black text-3xl sm:text-4xl text-slate-900 mt-1">Hi, {user.name?.split(" ")[0] || "there"}.</h1>
            <p className="text-sm text-slate-600 mt-1">Acting as <b>{ROLE_LABELS[role]}</b>.</p>
          </div>
          {(user.roles || []).length > 1 && (
            <div className="flex gap-1 flex-wrap" data-testid="role-tabs">
              {user.roles.map(r => (
                <Button
                  key={r}
                  variant={r === role ? "default" : "outline"}
                  size="sm"
                  className={r === role ? "bg-[#1D4ED8] hover:bg-[#1E40AF] text-white" : ""}
                  data-testid={`role-tab-${r}`}
                  onClick={async () => { await api.post("/auth/switch-role", { role: r }); window.location.reload(); }}
                >
                  {ROLE_LABELS[r]}
                </Button>
              ))}
            </div>
          )}
        </div>

        {role === "customer" && <CustomerView />}
        {role === "driver" && <DriverView />}
        {role === "dispatcher" && <DispatcherView />}
        {role === "admin" && <AdminView />}
      </section>
    </Layout>
  );
}
